function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useMemo, useEffect, useCallback, useRef } from 'react';
import { matchPath, useHistory } from 'react-router-dom';
import { isEqual } from 'lodash';
import { routes } from "../../../constants";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { selectors } from "../../../store/state-selectors";
import { useStore, useStoreActions } from "../../../store/store-context";
import { LogBiEvent } from "../constants";
export const usePageViewTracking = () => {
  const {
    api
  } = useStoreActions();
  const business_id = useStore(selectors.user.businessId);
  const user_id = useStore(selectors.user.id);
  const business_type = useStore(selectors.user.isVendor) ? 'service_provider' : 'client';
  const user_agent = window.navigator.userAgent;
  const lastSentBiRef = useRef();
  const baseBiEventRef = useRef({});
  baseBiEventRef.current = {
    session_id: window._sessionId,
    viewport_height: window.innerHeight,
    viewport_width: window.innerWidth,
    business_id,
    release: window.GIT_SHA1,
    user_id,
    business_type,
    user_agent,
    type: LogBiEvent.PageView
  };
  const {
    BI_PAGE_VIEW_EVENTS
  } = useFeatureFlags();
  const history = useHistory();
  const logPageViewBIEvent = useCallback(pageEvent => {
    if (isEqual(lastSentBiRef.current, pageEvent)) return;
    lastSentBiRef.current = pageEvent;
    const {
      page_name
    } = pageEvent;
    const nextBiEvent = _objectSpread(_objectSpread(_objectSpread({}, baseBiEventRef.current), pageEvent), {}, {
      hierarchy: [page_name]
    });
    api.logCatoBIEvent(nextBiEvent);
  }, []);
  const pagesBiConfig = useMemo(() => ({
    login: {
      logBI: () => logPageViewBIEvent({
        page_name: 'login'
      })
    },
    signUp: {
      logBI: () => logPageViewBIEvent({
        page_name: 'sign_up'
      })
    },
    agreementStudio: {
      logBI: () => logPageViewBIEvent({
        page_name: 'agreement_studio'
      })
    },
    redirect: {
      logBI: () => logPageViewBIEvent({
        page_name: 'redirect'
      })
    },
    agreement: {
      logBI: _ref => {
        let {
          stateParams
        } = _ref;
        const {
          relationshipId: relationship_id
        } = stateParams;
        logPageViewBIEvent({
          page_name: 'agreement',
          relationship_id
        });
      }
    },
    proposalTemplateStudio: {
      logBI: () => logPageViewBIEvent({
        page_name: 'proposal_template_studio'
      })
    },
    setPassword: {
      logBI: () => logPageViewBIEvent({
        page_name: 'set_password'
      })
    },
    resetPassword: {
      logBI: () => logPageViewBIEvent({
        page_name: 'reset_password'
      })
    },
    allSet: {
      logBI: _ref2 => {
        let {
          stateParams
        } = _ref2;
        const {
          relationshipId: relationship_id
        } = stateParams;
        logPageViewBIEvent({
          page_name: 'all_set',
          relationship_id
        });
      }
    },
    notification: {
      logBI: _ref3 => {
        let {
          stateParams
        } = _ref3;
        const {
          notificationId: notification_id
        } = stateParams;
        logPageViewBIEvent({
          page_name: 'notification',
          notification_id
        });
      }
    },
    notificationAction: {
      logBI: () => logPageViewBIEvent({
        page_name: 'notification_action'
      })
    },
    home: {
      logBI: () => logPageViewBIEvent({
        page_name: 'dashboard'
      })
    },
    acceptInvitation: {
      logBI: () => logPageViewBIEvent({
        page_name: 'accept_invitation'
      })
    },
    invoices: {
      logBI: _ref4 => {
        let {
          stateParams
        } = _ref4;
        const {
          relationshipId: relationship_id
        } = stateParams;
        logPageViewBIEvent({
          page_name: 'agreement_invoices',
          relationship_id
        });
      }
    },
    billingHub: {
      logBI: _ref5 => {
        let {
          stateParams
        } = _ref5;
        const {
          relationshipId: relationship_id
        } = stateParams;
        logPageViewBIEvent({
          page_name: 'agreement_billing_hub',
          relationship_id
        });
      }
    },
    activityLog: {
      logBI: _ref6 => {
        let {
          stateParams
        } = _ref6;
        const {
          relationshipId: relationship_id
        } = stateParams;
        logPageViewBIEvent({
          page_name: 'agreement_activity_log',
          relationship_id
        });
      }
    },
    paymentMethods: {
      logBI: _ref7 => {
        let {
          stateParams
        } = _ref7;
        const {
          relationshipId: relationship_id
        } = stateParams;
        logPageViewBIEvent({
          page_name: 'agreement_payment_methods',
          relationship_id
        });
      }
    },
    draft: {
      logBI: _ref8 => {
        let {
          stateParams
        } = _ref8;
        const {
          draftId: draft_id
        } = stateParams;
        logPageViewBIEvent({
          page_name: 'proposal_draft',
          draft_id
        });
      }
    },
    proposalTemplate: {
      logBI: _ref9 => {
        let {
          stateParams
        } = _ref9;
        const {
          templateId: template_id
        } = stateParams;
        logPageViewBIEvent({
          page_name: 'proposal_template',
          template_id
        });
      }
    },
    clientOnboarding: {
      logBI: _ref10 => {
        let {
          stateParams
        } = _ref10;
        const {
          relationshipId: relationship_id
        } = stateParams;
        logPageViewBIEvent({
          page_name: 'client_onboarding',
          relationship_id
        });
      }
    },
    expiredProposalPage: {
      logBI: () => logPageViewBIEvent({
        page_name: 'expired_proposal_page'
      })
    },
    canceledProposalPage: {
      logBI: () => logPageViewBIEvent({
        page_name: 'canceled_proposal_page'
      })
    },
    noPermissionsRelationshipAlreadyApproved: {
      logBI: () => logPageViewBIEvent({
        page_name: 'no_permissions_relationship_already_approved'
      })
    },
    allSignaturesAcquired: {
      logBI: () => logPageViewBIEvent({
        page_name: 'all_signatures_acquired'
      })
    },
    agreementsDashboard: {
      logBI: () => logPageViewBIEvent({
        page_name: 'agreements'
      })
    },
    proposalsDashboard: {
      logBI: () => logPageViewBIEvent({
        page_name: 'proposals'
      })
    },
    draftsDashboard: {
      logBI: () => logPageViewBIEvent({
        page_name: 'proposals_drafts'
      })
    },
    archivedProposals: {
      logBI: () => logPageViewBIEvent({
        page_name: 'proposals_archived'
      })
    },
    proposalTemplates: {
      logBI: () => logPageViewBIEvent({
        page_name: 'proposals_templates'
      })
    },
    proposalDefaults: {
      logBI: () => logPageViewBIEvent({
        page_name: 'proposals_default_settings'
      })
    },
    invoicesDashboard: {
      logBI: () => logPageViewBIEvent({
        page_name: 'invoices'
      })
    },
    payoutsDashboard: {
      logBI: () => logPageViewBIEvent({
        page_name: 'payouts'
      })
    },
    globalBillingHub: {
      logBI: () => logPageViewBIEvent({
        page_name: 'billing'
      })
    },
    availableApps: {
      logBI: () => logPageViewBIEvent({
        page_name: 'available_apps'
      })
    },
    connectedApps: {
      logBI: () => logPageViewBIEvent({
        page_name: 'connected_apps'
      })
    },
    serviceLibrary: {
      logBI: () => logPageViewBIEvent({
        page_name: 'services'
      })
    },
    bundlesLibrary: {
      logBI: () => logPageViewBIEvent({
        page_name: 'bundles'
      })
    },
    importServices: {
      logBI: () => logPageViewBIEvent({
        page_name: 'services_import'
      })
    },
    importContacts: {
      logBI: () => logPageViewBIEvent({
        page_name: 'contacts_import'
      })
    },
    importAgreements: {
      logBI: () => logPageViewBIEvent({
        page_name: 'agreements_import'
      })
    },
    anchorBilling: {
      logBI: () => logPageViewBIEvent({
        page_name: 'anchor_billing'
      })
    },
    vendorDashboard: {
      logBI: () => logPageViewBIEvent({
        page_name: 'vendor_dashboard'
      })
    },
    team: {
      logBI: () => logPageViewBIEvent({
        page_name: 'team'
      })
    },
    businessDetails: {
      logBI: () => logPageViewBIEvent({
        page_name: 'business_details'
      })
    },
    profile: {
      logBI: () => logPageViewBIEvent({
        page_name: 'profile'
      })
    },
    notifications: {
      logBI: () => logPageViewBIEvent({
        page_name: 'notification_settings'
      })
    },
    qboSetup: {
      logBI: () => logPageViewBIEvent({
        page_name: 'integration_setup_qbo'
      })
    },
    xeroSetup: {
      logBI: () => logPageViewBIEvent({
        page_name: 'integration_setup_xero'
      })
    },
    slackIntegration: {
      logBI: () => logPageViewBIEvent({
        page_name: 'integration_slack'
      })
    },
    hubspotIntegration: {
      logBI: () => logPageViewBIEvent({
        page_name: 'integration_hubspot'
      })
    },
    financialCentsSetup: {
      logBI: () => logPageViewBIEvent({
        page_name: 'integration_setup_financial_cents'
      })
    },
    karbonSetup: {
      logBI: () => logPageViewBIEvent({
        page_name: 'integration_setup_karbon'
      })
    },
    clientHubSetup: {
      logBI: () => logPageViewBIEvent({
        page_name: 'integration_setup_client_hub'
      })
    },
    keeperSetup: {
      logBI: () => logPageViewBIEvent({
        page_name: 'integration_setup_keeper'
      })
    },
    contacts: {
      logBI: () => logPageViewBIEvent({
        page_name: 'contacts'
      })
    },
    referrals: {
      logBI: () => logPageViewBIEvent({
        page_name: 'referrals'
      })
    },
    kyb: {
      logBI: () => logPageViewBIEvent({
        page_name: 'kyb'
      })
    },
    reports: {
      logBI: () => logPageViewBIEvent({
        page_name: 'reports'
      })
    },
    revenueReport: {
      logBI: () => logPageViewBIEvent({
        page_name: 'reports_revenue_breakdown'
      })
    },
    activeServicesReport: {
      logBI: () => logPageViewBIEvent({
        page_name: 'reports_active_services'
      })
    },
    remainingCreditReport: {
      logBI: () => logPageViewBIEvent({
        page_name: 'reports_remaining_credit'
      })
    },
    futureInvoicesReport: {
      logBI: () => logPageViewBIEvent({
        page_name: 'reports_future_invoices'
      })
    },
    proposalsReport: {
      logBI: () => logPageViewBIEvent({
        page_name: 'reports_sent_proposals'
      })
    },
    additionalPaymentProcessorInfo: {
      logBI: () => logPageViewBIEvent({
        page_name: 'additional_payment_processor_info'
      })
    },
    anchorApiKeys: {
      logBI: () => logPageViewBIEvent({
        page_name: 'api_keys'
      })
    },
    clientOnboardingStepWelcome: {
      logBI: _ref11 => {
        let {
          stateParams
        } = _ref11;
        const {
          relationshipId: relationship_id
        } = stateParams;
        logPageViewBIEvent({
          page_name: 'client_onboarding_step_welcome',
          relationship_id
        });
      }
    },
    clientOnboardingStepSelectAPackage: {
      logBI: _ref12 => {
        let {
          stateParams
        } = _ref12;
        const {
          relationshipId: relationship_id
        } = stateParams;
        logPageViewBIEvent({
          page_name: 'client_onboarding_step_select_package',
          relationship_id
        });
      }
    },
    clientOnboardingStepReviewServices: {
      logBI: _ref13 => {
        let {
          stateParams
        } = _ref13;
        const {
          relationshipId: relationship_id
        } = stateParams;
        logPageViewBIEvent({
          page_name: 'client_onboarding_step_review_services',
          relationship_id
        });
      }
    },
    clientOnboardingStepApproveTerms: {
      logBI: _ref14 => {
        let {
          stateParams
        } = _ref14;
        const {
          relationshipId: relationship_id
        } = stateParams;
        logPageViewBIEvent({
          page_name: 'client_onboarding_step_approve_terms',
          relationship_id
        });
      }
    },
    clientOnboardingStepAccountantAccess: {
      logBI: _ref15 => {
        let {
          stateParams
        } = _ref15;
        const {
          relationshipId: relationship_id
        } = stateParams;
        logPageViewBIEvent({
          page_name: 'client_onboarding_step_accountant_access',
          relationship_id
        });
      }
    },
    clientOnboardingStepPaymentMethod: {
      logBI: _ref16 => {
        let {
          stateParams
        } = _ref16;
        const {
          relationshipId: relationship_id
        } = stateParams;
        logPageViewBIEvent({
          page_name: 'client_onboarding_step_payment_method',
          relationship_id
        });
      }
    },
    clientOnboardingStepIntroVideo: {
      logBI: _ref17 => {
        let {
          stateParams
        } = _ref17;
        const {
          relationshipId: relationship_id
        } = stateParams;
        logPageViewBIEvent({
          page_name: 'client_onboarding_step_intro_video',
          relationship_id
        });
      }
    },
    clientOnboardingStepAllSet: {
      logBI: _ref18 => {
        let {
          stateParams
        } = _ref18;
        const {
          relationshipId: relationship_id
        } = stateParams;
        logPageViewBIEvent({
          page_name: 'client_onboarding_step_all_set',
          relationship_id
        });
      }
    }
  }), []);
  useEffect(() => {
    if (!BI_PAGE_VIEW_EVENTS) return;
    const trackPageView = location => {
      const queryParams = Object.fromEntries(new URLSearchParams(location.search));
      const page_name = location.pathname;
      let matchedRouteId = null;
      let stateParams = {};
      for (const [key, pathPattern] of Object.entries(routes)) {
        if (!pathPattern) continue;
        const pattern = typeof pathPattern === 'function' ? pathPattern() : pathPattern;
        const result = matchPath(page_name, {
          path: pattern,
          exact: true
        });
        if (result) {
          matchedRouteId = key;
          stateParams = result.params;
          break;
        }
      }
      const matchedRoute = matchedRouteId ? pagesBiConfig[matchedRouteId] : null;
      matchedRoute === null || matchedRoute === void 0 || matchedRoute.logBI({
        stateParams,
        queryParams
      });
    };

    // Track the initial page view
    trackPageView(history.location);
    const unsubscribe = history.listen(trackPageView);
    return () => unsubscribe();
  }, [BI_PAGE_VIEW_EVENTS]);
};